import "./users-permissions.scss";
import React, { useState, useEffect, useCallback } from "react";
import {
  GSListPage,
  GSInput,
  GSItemList,
  GSEmptyList,
} from "golfstatus_react_components";
import {
  faTimesCircle,
  faPlus,
  faInfoCircle,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentOrganization } from "../../reducers/organizationSlice";
import {
  selectOrganizationMembers,
  selectLoading,
  clearNotifications,
  selectUsersNotifications, getOrganizationUsers,
} from "../../reducers/userSlice";
import MemberLineItem from "./member-line-item";
import { Outlet, useNavigate } from "react-router-dom";
import { useMatch } from "react-router-dom";
import { useNotificationAction } from "../../hooks/notificationHooks";
import { addNotification } from "../../reducers/appSlice";

const UsersPermissions = () => {
  const dispatch = useDispatch();
  const orgMembers = useSelector(selectOrganizationMembers);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const match = useMatch("/:id/permissions/*");
  const matchEdit = useMatch("/:id/permissions/:memberID/*");
  const loading = useSelector(selectLoading);
  const userNotifications = useSelector(selectUsersNotifications);

  const loadData = useCallback(() => {
    if (currentOrganization.id) {
      dispatch(getOrganizationUsers(currentOrganization?.id));
    }
  }, [dispatch, currentOrganization]);
  useEffect(() => {
    loadData();
  }, [loadData, currentOrganization]);

  const clearText = () => {
    setName("");
  };
  const searchChanged = (e) => {
    setName(e.target.value);
  };
  const addUser = () => {
    dispatch(clearNotifications());
    navigate("add");
  };
  const editUser = (user) => {
    dispatch(clearNotifications());
    if (!isSidePanelOpen()) {
      navigate(`${user.id}/edit-member`);
    } else {
      navigate(``);
    }
  };

  useNotificationAction(
    "saved",
    userNotifications,
    (notification) => {
      dispatch(clearNotifications());
      dispatch(addNotification(notification));
    },
    "Member Invitation"
  );

  const typeInfo = (e) => {
    navigate(`type-info`);
    e?.stopPropagation?.();
  };

  const getPageActions = () => {
    return [
      {
        buttonTitle: "Add User",
        type: "black mobile-icon",
        actionIcon: faPlus,
        actionClick: addUser,
      },
      {
        type: "light-grey mobile-icon",
        actionIcon: faInfoCircle,
        actionClick: typeInfo,
      },
    ];
  };
  const isSidePanelOpen = () => {
    let sidePanelRoutes = [
      "add",
      "edit-member",
      "delete-member",
      "type-info",
    ];
    return sidePanelRoutes.includes(
      matchEdit?.params?.["*"] ? matchEdit?.params?.["*"] : match?.params?.["*"]
    );
  };
  const getRouter = () => {
    return <Outlet />;
  };
  const getListPage = (title) => {
    const page = {
      title: title,
      getDefaultSearch: getDefaultSearch,
      getItemList: getItemList,
      getPageActions: getPageActions,
      isSidePanelOpen: isSidePanelOpen,
      getRouter: getRouter,
    };
    return <GSListPage {...page} />;
  };

  const getEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="Create & Manage Users"
        detail="Create users and adjust permission levels for your organization."
        actions={[
          {
            title: "Add User",
            onClick: addUser,
            buttonIcon: faPlus,
            type: "black",
          },
        ]}
      />
    );
  };
  const getEmptySearchMessage = () => {
    return (
      <GSEmptyList
        title="We couldn’t find the user you’re looking for…"
        detail="Try changing the search term or add a new user."
        actions={[
          {
            title: "Clear Search",
            onClick: clearText,
            type: "light-grey",
          },
          {
            title: "Add User",
            buttonIcon: faPlus,
            onClick: addUser,
            type: "black",
          },
        ]}
      />
    );
  };
  const getItemList = () => {
    return (
      <GSItemList
        items={orgMembers?.filter?.((member) =>
          member?.user?.name?.toLowerCase?.()?.includes?.(name?.toLowerCase())
        )}
        listItem={(member) => <MemberLineItem {...member} />}
        type="vertical selectable"
        emptyMessage={name ? getEmptySearchMessage() : getEmptyListMessage()}
        itemSelected={editUser}
        loading={loading?.includes?.("getOrganizationUsers") || loading?.includes?.("resendUserInvitation")}
        loadingMainText="Loading"
      ></GSItemList>
    );
  };
  const getDefaultSearch = () => {
    return (
      <div className="search">
        <GSInput
          textValue={name}
          leftIcon={faSearch}
          rightIconClick={clearText}
          rightIcon={faTimesCircle}
          onChange={searchChanged}
          placeholder="Search Users..."
        ></GSInput>
      </div>
    );
  };
  return (
    <users-permissions>{getListPage("Users & Permissions")}</users-permissions>
  );
};

export default UsersPermissions;
